import React, { useState, useMemo } from 'react';

export interface EmailListInputProps {
  label?: string;
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  helperText?: string;
  disabled?: boolean;
  className?: string;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function parseEmailList(raw?: string): string[] {
  if (!raw || typeof raw !== 'string') return [];
  return raw
    .split(/[,;\s]+/)
    .map((e) => e.trim())
    .filter((e) => e.length > 0 && e.includes('@'));
}

export const EmailListInput: React.FC<EmailListInputProps> = ({
  label,
  value = '',
  onChange,
  placeholder = 'Enter email address...',
  helperText,
  disabled = false,
  className = '',
}) => {
  const [currentInput, setCurrentInput] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  // Parse comma/semicolon-separated string into a deduplicated list
  const emails = useMemo(() => {
    const parsed = parseEmailList(value);
    const seen = new Set<string>();
    const unique: string[] = [];
    for (const em of parsed) {
      const lower = em.toLowerCase();
      if (!seen.has(lower)) {
        seen.add(lower);
        unique.push(em);
      }
    }
    return unique;
  }, [value]);

  const handleAdd = () => {
    const raw = currentInput.trim();
    if (!raw) return;

    // Support entering single email or pasting multiple comma/semicolon/space-separated emails
    const candidates = raw
      .split(/[,;\s]+/)
      .map((e) => e.trim())
      .filter((e) => e.length > 0);

    const validNewEmails: string[] = [];
    const invalidList: string[] = [];
    const duplicateList: string[] = [];

    const currentSet = new Set(emails.map((e) => e.toLowerCase()));

    for (const cand of candidates) {
      if (!EMAIL_REGEX.test(cand)) {
        invalidList.push(cand);
      } else if (currentSet.has(cand.toLowerCase()) || validNewEmails.some((e) => e.toLowerCase() === cand.toLowerCase())) {
        duplicateList.push(cand);
      } else {
        validNewEmails.push(cand);
      }
    }

    if (invalidList.length > 0) {
      setErrorMsg(`Invalid email format: "${invalidList[0]}"`);
      return;
    }

    if (duplicateList.length > 0 && validNewEmails.length === 0) {
      setErrorMsg(`"${duplicateList[0]}" is already in the Carbon Copy list.`);
      return;
    }

    if (validNewEmails.length > 0) {
      const updatedList = [...emails, ...validNewEmails];
      onChange(updatedList.join(', '));
      setCurrentInput('');
      setErrorMsg('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    }
  };

  const handleRemove = (emailToRemove: string) => {
    if (disabled) return;
    const filtered = emails.filter((e) => e.toLowerCase() !== emailToRemove.toLowerCase());
    onChange(filtered.join(', '));
  };

  const handleClearAll = () => {
    if (disabled) return;
    onChange('');
    setErrorMsg('');
  };

  return (
    <div className={`space-y-1.5 ${className}`}>
      {/* Header with label & count badge */}
      <div className="flex items-center justify-between">
        {label && (
          <label className="label text-xs !mb-0 font-medium text-gray-700 flex items-center gap-1.5">
            <span>{label}</span>
            {emails.length > 0 && (
              <span className="text-[10px] font-semibold text-brand-700 bg-brand-50 border border-brand-200 px-1.5 py-0.2 rounded-full">
                {emails.length} {emails.length === 1 ? 'recipient' : 'recipients'}
              </span>
            )}
          </label>
        )}
        {emails.length > 1 && !disabled && (
          <button
            type="button"
            onClick={handleClearAll}
            className="text-[11px] text-gray-400 hover:text-red-600 transition-colors"
          >
            Clear all
          </button>
        )}
      </div>

      {/* Input row with Add button */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <input
            type="email"
            value={currentInput}
            onChange={(e) => {
              setCurrentInput(e.target.value);
              if (errorMsg) setErrorMsg('');
            }}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            className={`input text-xs py-1.5 ${
              errorMsg ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''
            }`}
          />
        </div>
        <button
          type="button"
          onClick={handleAdd}
          disabled={disabled || !currentInput.trim()}
          className="btn btn-primary text-xs py-1.5 px-3 shrink-0 flex items-center gap-1.5 disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed shadow-xs"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
          </svg>
          <span>Add</span>
        </button>
      </div>

      {/* Error Message */}
      {errorMsg && (
        <p className="text-[11px] text-red-600 mt-1 flex items-center gap-1 font-medium">
          <svg className="w-3 h-3 text-red-500 shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          <span>{errorMsg}</span>
        </p>
      )}

      {/* List of Email Chips */}
      {emails.length > 0 ? (
        <div className="flex flex-wrap gap-1.5 pt-1 max-h-32 overflow-y-auto">
          {emails.map((email) => (
            <span
              key={email}
              className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-md text-xs font-medium bg-slate-100 text-slate-800 border border-slate-200/90 shadow-2xs group hover:bg-slate-200/70 transition-colors"
            >
              <svg
                className="w-3 h-3 text-slate-400 group-hover:text-slate-600 shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207"
                />
              </svg>
              <span className="truncate max-w-[220px]" title={email}>
                {email}
              </span>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => handleRemove(email)}
                  className="text-slate-400 hover:text-red-600 hover:bg-red-50 rounded p-0.5 transition-colors focus:outline-none"
                  title={`Remove ${email}`}
                >
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </span>
          ))}
        </div>
      ) : (
        <div className="pt-0.5 text-[11px] text-gray-400 italic">
          No Carbon Copy recipients added.
        </div>
      )}

      {/* Helper text */}
      {helperText && (
        <span className="text-[11px] text-gray-400 block pt-0.5">
          {helperText}
        </span>
      )}
    </div>
  );
};

